/**
 * Lenavio Succession — per-position detail.
 *
 * Renders:
 *   * Position metadata + criticality + risk badge
 *   * Current holder card
 *   * Candidate bench list (readiness + perf/pot ratings + edit)
 *   * "Add candidate" picker (searches active employees)
 *   * "AI Recommend" modal — Claude suggests up to 5 candidates
 *
 * HR/admin only — RoleGate at the route boundary.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Plus,
  Sparkles,
  Trash2,
  UserCheck,
} from "lucide-react";

import { listEmployees } from "@/api/employees";
import {
  addCandidate,
  aiRecommend,
  deleteCandidate,
  getPosition,
  updateCandidate,
  type AIRecommendation,
  type KeyPosition,
  type Readiness,
} from "@/api/succession";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

const READINESS_OPTIONS: { value: Readiness; label: string }[] = [
  { value: "ready_now", label: "Ready now" },
  { value: "ready_1yr", label: "Ready ~1 year" },
  { value: "ready_2yr", label: "Ready ~2 years" },
  { value: "development_needed", label: "Development needed" },
];

const READINESS_VARIANT: Record<Readiness, "success" | "warning" | "info" | "danger"> = {
  ready_now: "success",
  ready_1yr: "warning",
  ready_2yr: "warning",
  development_needed: "danger",
};

interface EmployeeOption {
  id: string;
  name: string;
  title: string;
  department_id: string | null;
}

export function PositionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();

  const [position, setPosition] = useState<KeyPosition | null>(null);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [adding, setAdding] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const refresh = async () => {
    if (!id) return;
    try {
      setPosition(await getPosition(id));
    } catch {
      addToast("error", "Could not load position.");
    }
  };

  useEffect(() => {
    void refresh();
    void listEmployees(1, 200).then((res) =>
      setEmployees(
        res.items.map((e) => ({
          id: e.id,
          name: `${e.first_name} ${e.last_name}`,
          title: e.title || "—",
          department_id:
            (e as unknown as { department_id?: string | null })
              .department_id ?? null,
        })),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRemove = async (candidateId: string) => {
    if (!confirm("Remove this candidate from the bench?")) return;
    try {
      await deleteCandidate(candidateId);
      addToast("success", "Candidate removed.");
      void refresh();
    } catch {
      addToast("error", "Could not remove candidate.");
    }
  };

  if (!position) {
    return <div className="h-64 rounded bg-border/30 animate-pulse" />;
  }

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title={position.title}
        subtitle={position.department || "Key position"}
        actions={
          <div className="flex gap-2">
            <Link
              to="/succession"
              className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All positions
            </Link>
            <Button variant="secondary" onClick={() => setAiOpen(true)}>
              <Sparkles className="h-4 w-4" />
              AI Recommend
            </Button>
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" />
              Add candidate
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Criticality
          </p>
          <div className="mt-1 flex items-center gap-2">
            <Badge
              variant={
                position.criticality === "critical"
                  ? "danger"
                  : position.criticality === "important"
                    ? "warning"
                    : "default"
              }
            >
              {position.criticality}
            </Badge>
            <Badge
              variant={
                position.risk === "red"
                  ? "danger"
                  : position.risk === "yellow"
                    ? "warning"
                    : "success"
              }
            >
              {position.risk === "red"
                ? "High risk"
                : position.risk === "yellow"
                  ? "Watch"
                  : "Healthy"}
            </Badge>
          </div>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Current holder
          </p>
          <p className="text-sm text-text-primary mt-1">
            {position.current_holder_name || (
              <span className="text-danger">Vacant — top priority</span>
            )}
          </p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Bench depth
          </p>
          <p className="text-2xl font-semibold font-heading text-text-primary">
            {position.candidate_count}
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            candidate{position.candidate_count === 1 ? "" : "s"}
          </p>
        </Card>
      </div>

      {position.notes && (
        <Card className="mb-4">
          <p className="text-xs uppercase tracking-wide text-text-muted mb-1">
            Notes
          </p>
          <p className="text-sm text-text-primary whitespace-pre-wrap">
            {position.notes}
          </p>
        </Card>
      )}

      <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted mb-2">
        Bench
      </h3>
      {position.candidates.length === 0 ? (
        <Card className="text-center py-10">
          <UserCheck className="h-8 w-8 text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-primary font-medium">
            No bench yet
          </p>
          <p className="text-xs text-text-muted">
            Add internal candidates manually or click AI Recommend.
          </p>
        </Card>
      ) : (
        <ul className="space-y-2">
          {position.candidates.map((c) => (
            <CandidateRow
              key={c.id}
              candidateId={c.id}
              name={c.candidate_name}
              readiness={c.readiness}
              performance={c.performance_rating}
              potential={c.potential_rating}
              onRemove={() => handleRemove(c.id)}
              onChanged={() => refresh()}
            />
          ))}
        </ul>
      )}

      {adding && (
        <AddCandidateModal
          employees={employees}
          excludeIds={[
            ...position.candidates.map((c) => c.candidate_employee_id),
            position.current_holder_id || "",
          ]}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await refresh();
          }}
          positionId={position.id}
        />
      )}

      {aiOpen && (
        <AIRecommendModal
          positionId={position.id}
          existingIds={[
            ...position.candidates.map((c) => c.candidate_employee_id),
            position.current_holder_id || "",
          ]}
          onClose={() => setAiOpen(false)}
          onAccepted={async () => {
            setAiOpen(false);
            await refresh();
          }}
        />
      )}
    </motion.div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Candidate row (with inline editor)
// ════════════════════════════════════════════════════════════════════


function CandidateRow({
  candidateId,
  name,
  readiness,
  performance,
  potential,
  onRemove,
  onChanged,
}: {
  candidateId: string;
  name: string;
  readiness: Readiness;
  performance: number | null;
  potential: number | null;
  onRemove: () => void;
  onChanged: () => void;
}) {
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const [r, setR] = useState<Readiness>(readiness);
  const [perf, setPerf] = useState<number | null>(performance);
  const [pot, setPot] = useState<number | null>(potential);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateCandidate(candidateId, {
        readiness: r,
        performance_rating: (perf ?? undefined) as 1 | 2 | 3 | undefined,
        potential_rating: (pot ?? undefined) as 1 | 2 | 3 | undefined,
      });
      addToast("success", "Updated.");
      setOpen(false);
      onChanged();
    } catch {
      addToast("error", "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-text-primary">{name}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant={READINESS_VARIANT[readiness]}>
              {READINESS_OPTIONS.find((o) => o.value === readiness)?.label}
            </Badge>
            {performance !== null && (
              <span className="text-[11px] text-text-muted">
                Perf {performance}/3
              </span>
            )}
            {potential !== null && (
              <span className="text-[11px] text-text-muted">
                Pot {potential}/3
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Cancel" : "Edit"}
          </Button>
          <Button variant="ghost" size="sm" onClick={onRemove} aria-label="Remove">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <Select
            label="Readiness"
            value={r}
            onChange={(e) => setR(e.target.value as Readiness)}
            options={READINESS_OPTIONS}
          />
          <div className="grid grid-cols-2 gap-3">
            <RatingPicker label="Performance" value={perf} onChange={setPerf} />
            <RatingPicker label="Potential" value={pot} onChange={setPot} />
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={save} isLoading={saving}>
              Save
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}


function RatingPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1">
        {label}
      </label>
      <div className="flex items-center gap-1">
        {[1, 2, 3].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(value === n ? null : n)}
            className={`h-8 w-8 rounded-md text-xs font-medium border transition-colors ${
              value === n
                ? "bg-accent text-white border-accent"
                : "bg-surface text-text-muted border-border hover:border-accent/50"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Add candidate modal
// ════════════════════════════════════════════════════════════════════


function AddCandidateModal({
  employees,
  excludeIds,
  positionId,
  onClose,
  onAdded,
}: {
  employees: EmployeeOption[];
  excludeIds: string[];
  positionId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { addToast } = useToast();
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<EmployeeOption | null>(null);
  const [readiness, setReadiness] = useState<Readiness>("development_needed");
  const [strengths, setStrengths] = useState("");
  const [development, setDevelopment] = useState("");
  const [perf, setPerf] = useState<number | null>(null);
  const [pot, setPot] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees
      .filter((e) => !exclude.has(e.id))
      .filter(
        (e) =>
          !q ||
          e.name.toLowerCase().includes(q) ||
          e.title.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [employees, exclude, search]);

  const save = async () => {
    if (!picked) {
      addToast("error", "Pick an employee first.");
      return;
    }
    setSubmitting(true);
    try {
      await addCandidate(positionId, {
        candidate_employee_id: picked.id,
        readiness,
        strengths: strengths.trim() || undefined,
        development_areas: development.trim() || undefined,
        performance_rating: (perf ?? undefined) as 1 | 2 | 3 | undefined,
        potential_rating: (pot ?? undefined) as 1 | 2 | 3 | undefined,
      });
      addToast("success", "Candidate added.");
      onAdded();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not add candidate.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add succession candidate" size="lg">
      <div className="space-y-3">
        {!picked ? (
          <>
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employees…"
            />
            <div className="max-h-64 overflow-y-auto divide-y divide-border border border-border rounded-lg bg-background">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-text-muted">
                  No matches.
                </div>
              ) : (
                filtered.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setPicked(e)}
                    className="w-full text-left px-3 py-2 hover:bg-surface flex items-center justify-between"
                  >
                    <span className="text-sm text-text-primary">{e.name}</span>
                    <span className="text-[11px] text-text-muted">
                      {e.title}
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg bg-accent/5 border border-accent/20 px-3 py-2 flex items-center justify-between">
              <span className="text-sm text-text-primary">
                <strong>{picked.name}</strong>
                <span className="text-text-muted text-xs ml-2">
                  {picked.title}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="text-xs text-accent hover:underline"
              >
                Change
              </button>
            </div>
            <Select
              label="Readiness"
              value={readiness}
              onChange={(e) => setReadiness(e.target.value as Readiness)}
              options={READINESS_OPTIONS}
            />
            <div className="grid grid-cols-2 gap-3">
              <RatingPicker label="Performance" value={perf} onChange={setPerf} />
              <RatingPicker label="Potential" value={pot} onChange={setPot} />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Strengths
              </label>
              <textarea
                value={strengths}
                onChange={(e) => setStrengths(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Development areas
              </label>
              <textarea
                value={development}
                onChange={(e) => setDevelopment(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={save} isLoading={submitting}>
                Add to bench
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════════════
// AI recommend modal
// ════════════════════════════════════════════════════════════════════


function AIRecommendModal({
  positionId,
  existingIds,
  onClose,
  onAccepted,
}: {
  positionId: string;
  existingIds: string[];
  onClose: () => void;
  onAccepted: () => void;
}) {
  const { addToast } = useToast();
  const [extra, setExtra] = useState("");
  const [running, setRunning] = useState(false);
  const [out, setOut] = useState<AIRecommendation[] | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);

  const exclude = useMemo(() => new Set(existingIds), [existingIds]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await aiRecommend(positionId, extra.trim() || undefined);
      setOut(r.recommendations.filter((x) => !exclude.has(x.employee_id)));
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "AI recommend failed.";
      addToast("error", detail);
    } finally {
      setRunning(false);
    }
  };

  const accept = async (rec: AIRecommendation) => {
    setAccepting(rec.employee_id);
    try {
      await addCandidate(positionId, {
        candidate_employee_id: rec.employee_id,
        readiness: rec.readiness,
        strengths: rec.rationale || undefined,
      });
      addToast("success", `${rec.employee_name} added to bench.`);
      onAccepted();
    } catch {
      addToast("error", "Could not add candidate.");
    } finally {
      setAccepting(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="AI Recommend candidates" size="lg">
      {!out ? (
        <div className="space-y-3">
          <p className="text-sm text-text-muted">
            We'll suggest up to 5 internal candidates ranked by fit. Add
            any you want — nothing's saved automatically.
          </p>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Extra context (optional)
            </label>
            <textarea
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              rows={4}
              placeholder="What's the role profile? Any tenure / domain preferences?"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button onClick={run} isLoading={running}>
              <Sparkles className="h-4 w-4" />
              Generate suggestions
            </Button>
          </div>
        </div>
      ) : out.length === 0 ? (
        <div className="text-center py-6">
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-text-primary">
            The model didn't return any new candidates.
          </p>
          <p className="text-xs text-text-muted">
            Everyone it suggested is already on this bench.
          </p>
          <Button variant="ghost" className="mt-3" onClick={() => setOut(null)}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-text-muted">
            Click <strong>Add</strong> on any candidate to drop them onto the
            bench with the suggested readiness.
          </p>
          <ul className="divide-y divide-border border border-border rounded-lg bg-background">
            {out.map((r) => (
              <li
                key={r.employee_id}
                className="px-3 py-3 flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-text-primary">
                      {r.employee_name}
                    </p>
                    <Badge variant="info">Fit {r.fit_score}/100</Badge>
                    <Badge variant={READINESS_VARIANT[r.readiness]}>
                      {READINESS_OPTIONS.find((o) => o.value === r.readiness)
                        ?.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-text-muted mt-1">{r.rationale}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => accept(r)}
                  isLoading={accepting === r.employee_id}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setOut(null)}>
              Re-run
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
